import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import PageShell from '../components/PageShell';
import request from '../api';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend, Filler);

const ELECTRIC_BLUE = '#00C2FF';
const ELECTRIC_PURPLE = '#7B2FF7';
const MUTED_TEXT = 'rgba(245, 245, 247, 0.64)';
const GRID_COLOR = 'rgba(255, 255, 255, 0.08)';

const MONTH_LABELS = {
    '01': 'Ene', '02': 'Feb', '03': 'Mar', '04': 'Abr', '05': 'May', '06': 'Jun',
    '07': 'Jul', '08': 'Ago', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dic',
};

function formatMonthLabel(month) {
    const [year, m] = month.split('-');
    return `${MONTH_LABELS[m] || m} ${year.slice(2)}`;
}

function formatCurrency(value) {
    return `$${Math.round(Number(value) || 0).toLocaleString('es-MX')} mxn`;
}

function StatCard({ icon, label, value, sub }) {
    return (
        <Paper sx={{ p: { xs: 1.75, sm: 2.5 }, height: '100%' }}>
            <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} alignItems="flex-start">
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: { xs: 32, sm: 40 },
                        height: { xs: 32, sm: 40 },
                        borderRadius: 2,
                        bgcolor: 'rgba(123, 47, 247, 0.14)',
                        color: 'primary.main',
                        flexShrink: 0,
                    }}
                >
                    {icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: { xs: '0.68rem', sm: '0.75rem' } }}>
                        {label}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, lineHeight: 1.25, fontSize: { xs: '1.05rem', sm: '1.5rem' } }} noWrap>
                        {value}
                    </Typography>
                    {sub && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                            {sub}
                        </Typography>
                    )}
                </Box>
            </Stack>
        </Paper>
    );
}

function StoreStats() {
    const [token] = useState(localStorage.getItem('storeToken'));
    const storeName = localStorage.getItem('storeName');
    const [stats, setStats] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) {
            navigate('/store/login');
            return;
        }
        setLoading(true);
        request('/store/stats', { token })
            .then(setStats)
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, [token, navigate]);

    const logout = () => {
        localStorage.removeItem('storeToken');
        localStorage.removeItem('storeName');
        navigate('/store/login');
    };

    const nav = (
        <>
            <Button component={RouterLink} to="/store/dashboard" color="inherit">Volver al Panel</Button>
            <Button component={RouterLink} to="/store/settings" color="inherit">Datos de Tienda</Button>
            <Button onClick={logout} variant="outlined" color="inherit">Cerrar Sesión</Button>
        </>
    );

    const revenueChartData = stats && {
        labels: stats.monthly.map((m) => formatMonthLabel(m.month)),
        datasets: [
            {
                label: 'Ingresos',
                data: stats.monthly.map((m) => m.revenue),
                borderColor: ELECTRIC_BLUE,
                backgroundColor: 'rgba(0, 194, 255, 0.18)',
                pointBackgroundColor: ELECTRIC_BLUE,
                pointBorderColor: ELECTRIC_BLUE,
                tension: 0.35,
                fill: true,
            },
        ],
    };

    const salesCountChartData = stats && {
        labels: stats.monthly.map((m) => formatMonthLabel(m.month)),
        datasets: [
            {
                label: 'Ventas',
                data: stats.monthly.map((m) => m.count),
                backgroundColor: 'rgba(123, 47, 247, 0.65)',
                borderRadius: 6,
                maxBarThickness: 36,
            },
        ],
    };

    const weekdayChartData = stats && {
        labels: stats.weekday.map((w) => w.day),
        datasets: [
            {
                label: 'Ventas por día',
                data: stats.weekday.map((w) => w.count),
                backgroundColor: stats.weekday.map((w) =>
                    stats.bestWeekday && w.day === stats.bestWeekday.day
                        ? ELECTRIC_PURPLE
                        : 'rgba(123, 47, 247, 0.35)'
                ),
                borderRadius: 6,
                maxBarThickness: 42,
            },
        ],
    };

    const baseOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: 'rgba(10, 10, 11, 0.92)',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: GRID_COLOR,
                borderWidth: 1,
                padding: 10,
            },
        },
        scales: {
            x: {
                ticks: { color: MUTED_TEXT },
                grid: { color: 'transparent' },
            },
            y: {
                beginAtZero: true,
                ticks: { color: MUTED_TEXT, precision: 0 },
                grid: { color: GRID_COLOR },
            },
        },
    };

    const revenueOptions = {
        ...baseOptions,
        plugins: {
            ...baseOptions.plugins,
            tooltip: {
                ...baseOptions.plugins.tooltip,
                callbacks: {
                    label: (ctx) => `Ingresos: ${formatCurrency(ctx.parsed.y)}`,
                },
            },
        },
        scales: {
            ...baseOptions.scales,
            y: {
                ...baseOptions.scales.y,
                ticks: { color: MUTED_TEXT, callback: (value) => formatCurrency(value) },
            },
        },
    };

    return (
        <PageShell title={`Estadísticas${storeName ? `: ${storeName}` : ''}`} nav={nav}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                Panel de Ventas
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Analiza tu desempeño mes con mes.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {loading ? (
                <Stack alignItems="center" sx={{ py: 8 }}>
                    <CircularProgress />
                </Stack>
            ) : stats && stats.totals.totalSales === 0 ? (
                <Alert severity="info">
                    Aún no tienes ventas completadas. Cuando tus productos sean entregados, aquí verás tus estadísticas.
                </Alert>
            ) : stats ? (
                <>
                    <Grid container spacing={2.5} sx={{ mb: 3 }}>
                        <Grid item xs={6} md={3}>
                            <StatCard
                                icon={<PaidOutlinedIcon />}
                                label="Ingresos Totales"
                                value={formatCurrency(stats.totals.totalRevenue)}
                            />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <StatCard
                                icon={<ReceiptLongOutlinedIcon />}
                                label="Ventas Totales"
                                value={stats.totals.totalSales}
                            />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <StatCard
                                icon={<TrendingUpIcon />}
                                label="Promedio por Venta"
                                value={formatCurrency(stats.totals.avgSale)}
                            />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <StatCard
                                icon={<ScheduleOutlinedIcon />}
                                label="Tiempo Prom. en Venderse"
                                value={`${stats.totals.avgDaysToSell.toFixed(1)} días`}
                            />
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <StatCard
                                icon={<CalendarMonthOutlinedIcon />}
                                label="Mejor Mes"
                                value={stats.bestMonth ? formatMonthLabel(stats.bestMonth.month) : '—'}
                                sub={stats.bestMonth ? `${formatCurrency(stats.bestMonth.revenue)} · ${stats.bestMonth.count} ventas` : undefined}
                            />
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <StatCard
                                icon={<EventAvailableOutlinedIcon />}
                                label="Día con Más Ventas"
                                value={stats.bestWeekday ? stats.bestWeekday.day : '—'}
                                sub={stats.bestWeekday ? `${stats.bestWeekday.count} ventas · ${formatCurrency(stats.bestWeekday.revenue)}` : undefined}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2.5}>
                        <Grid item xs={12} md={7}>
                            <Paper sx={{ p: 2.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                                    Ingresos por Mes
                                </Typography>
                                <Box sx={{ height: 280 }}>
                                    <Line data={revenueChartData} options={revenueOptions} />
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Paper sx={{ p: 2.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                                    Ventas por Mes
                                </Typography>
                                <Box sx={{ height: 280 }}>
                                    <Bar data={salesCountChartData} options={baseOptions} />
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12}>
                            <Paper sx={{ p: 2.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                                    Ventas por Día de la Semana
                                </Typography>
                                <Box sx={{ height: 280 }}>
                                    <Bar data={weekdayChartData} options={baseOptions} />
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </>
            ) : null}
        </PageShell>
    );
}

export default StoreStats;
